import { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import { Plus, Car, Trash2, Calendar, MapPin, DollarSign, Edit3 } from "lucide-react";

import { toast } from "sonner";
import { Field } from "./Properties";
import MileageDetailModal from "../components/MileageDetailModal";

export default function Mileage() {
  const [logs, setLogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [startInEditMode, setStartInEditMode] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    property_id: "",
    purpose: "",
    miles: "",
    irs_rate: "",
    notes: "",
  });
  const { can = {} } = useAuth() || {};

  const load = () => {
    api.get(`/mileage?year=${year}`).then((r) => setLogs(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
  };

  useEffect(() => { load(); }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    const milesNum = parseFloat(form.miles);
    if (!milesNum || milesNum <= 0) return toast.error("Enter valid miles");
    if (!form.purpose.trim()) return toast.error("Purpose required");

    try {
      await api.post("/mileage", {
        ...form,
        miles: milesNum,
        property_id: form.property_id || null,
        irs_rate: form.irs_rate ? parseFloat(form.irs_rate) : null,
      });
      toast.success("Mileage logged");
      setShowForm(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        property_id: "",
        purpose: "",
        miles: "",
        irs_rate: "",
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log mileage");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this mileage entry?")) return;
    try {
      await api.delete(`/mileage/${id}`);
      toast.success("Entry removed");
      load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const totalMiles = logs.reduce((sum, l) => sum + (l.miles || 0), 0);
  const totalDeduction = logs.reduce((sum, l) => sum + (l.total_deduction || 0), 0);

  return (
    <Layout
      title="Mileage Tracker"
      subtitle={`Tax Year ${year} · IRS Standard Mileage Method`}
      hideNav={showForm || !!selectedTrip}
      actions={
        can?.edit && (
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="new-mileage-btn"
            className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5"
          >
            <Plus size={16} /> Log Trip
          </button>
        )
      }
    >
      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="card p-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold flex items-center gap-1.5">
              <span>Total Miles Driven</span>
              <HelpTip topic="mileage-tracker" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight mt-1">{totalMiles.toLocaleString()} mi</div>
            <div className="text-xs text-[#86868B] mt-1">{logs.length} business trips logged</div>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-[#1C3A5E]/10 text-[#1C3A5E] flex items-center justify-center">
            <Car size={24} />
          </div>
        </div>

        <div className="card p-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#86868B] font-semibold flex items-center gap-1.5">
              <span>Tax Deduction (IRS)</span>
              <HelpTip topic="mileage-tracker" />
            </div>
            <div className="text-3xl font-extrabold tracking-tight text-[#1f8e3a] mt-1">{formatCurrency(totalDeduction)}</div>
            <div className="text-xs text-[#86868B] mt-1">Deductible under Schedule E Auto & Travel</div>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-[#34C759]/15 text-[#1f8e3a] flex items-center justify-center">
            <DollarSign size={24} />
          </div>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card p-4 sm:p-6 mb-6 shadow-xl border border-[var(--border)] rounded-2xl animate-in fade-in slide-in-from-top-2 duration-200" data-testid="mileage-form">
          <h3 className="text-lg font-bold mb-4">Log Business Travel</h3>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testId="mileage-date" />
            <Field label="Miles" type="number" value={form.miles} onChange={(v) => setForm({ ...form, miles: v })} required testId="mileage-miles" />
            <Field label="Business Purpose" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} placeholder="e.g. Hardware store for plumbing repairs" required testId="mileage-purpose" helpTopic="mileage-tracker" />
            <div>
              <label className="label">Related Property (optional)</label>
              <select
                className="lapen-input"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                data-testid="mileage-property"
              >
                <option value="">— General / Portfolio-wide —</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <Field label="Custom IRS Rate (blank = standard $0.70)" type="number" value={form.irs_rate} onChange={(v) => setForm({ ...form, irs_rate: v })} testId="mileage-rate" helpTopic="mileage-tracker" />
            <Field label="Notes (optional)" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="mileage-notes" />


            <div className="sm:col-span-2 flex gap-3 mt-2">
              <button type="submit" className="btn-primary" data-testid="mileage-submit">Save Trip</button>
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-3 text-[#86868B] font-semibold">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Log list */}
      <div className="card overflow-hidden" data-testid="mileage-list">
        <div className="p-4 bg-[#F5F5F7]/60 border-b border-[#E5E5EA]/60 flex items-center justify-between">
          <span className="text-xs font-semibold text-[#86868B] uppercase tracking-wider">Trips for Tax Year {year}</span>
          <select
            className="text-xs border border-[#E5E5EA] rounded-lg px-2 py-1 bg-white font-medium"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {[2026, 2025, 2024].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="divide-y divide-[#E5E5EA]/60">
          {logs.length === 0 && (
            <div className="p-4">
              <EmptyState
                icon={Car}
                title={`No Trips Logged for ${year}`}
                description="Track business driving between properties, hardware stores, and tenant meetings at $0.70/mile (2025 IRS standard rate)."
                helpTopic="mileage-tracker"
                helpText="View IRS Mileage Rules"
                actionLabel="+ Log Business Trip"
                onAction={() => setShowForm(true)}
                testId="mileage-empty-state"
              />
            </div>
          )}

          {logs.map((l) => (
            <div
              key={l.id}
              className="p-4 flex items-center justify-between gap-4 hover:bg-[var(--surface-hover)] transition-colors cursor-pointer"
              onClick={() => {
                setSelectedTrip(l);
                setStartInEditMode(false);
              }}
              data-testid={`mileage-item-${l.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-2">
                  <span className="truncate">{l.purpose}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--muted)] mt-1 truncate">
                  <span>{formatDate(l.date)}</span>
                  <span>·</span>
                  <span>{l.property_name || "Portfolio-wide"}</span>
                  {l.notes && (
                    <>
                      <span>·</span>
                      <span className="italic truncate">{l.notes}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 sm:gap-4 text-right shrink-0">
                <div>
                  <div className="font-bold text-sm tabular-nums">{l.miles} mi</div>
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums">
                    {formatCurrency(l.total_deduction)}
                  </div>
                </div>

                {can?.edit && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTrip(l);
                      setStartInEditMode(true);
                    }}
                    data-testid={`mileage-edit-${l.id}`}
                    className="p-1.5 rounded-lg text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] transition-colors"
                    title="Edit trip"
                  >
                    <Edit3 size={16} />
                  </button>
                )}

                {can?.delete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(l.id);
                    }}
                    data-testid={`mileage-del-${l.id}`}
                    className="p-1.5 rounded-lg text-[var(--muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete trip"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trip Detail & Edit Modal */}
      {selectedTrip && (
        <MileageDetailModal
          trip={selectedTrip}
          properties={properties}
          startInEditMode={startInEditMode}
          canDelete={can?.delete}
          canEdit={can?.edit}
          onClose={() => {
            setSelectedTrip(null);
            setStartInEditMode(false);
          }}
          onDelete={(id) => {
            remove(id);
            setSelectedTrip(null);
          }}
          onUpdate={(updated) => {
            setLogs((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setSelectedTrip(updated);
          }}
        />
      )}
    </Layout>
  );
}
